import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, AlertCircle, ExternalLink, Check, Clock, Zap, Shield, Crown } from 'lucide-react';
import logo from '../assets/logo.jpg';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';
import { subscriptionService } from '../services/subscriptionService';

interface SubscriptionInfo {
  plan: 'trial' | 'pro' | 'free' | 'none';
  status: 'trialing' | 'active' | 'expired' | 'canceled';
  label: string;
  daysRemaining: number;
  expiryDate: number | null;
  type: string;
}

const BillingPage: React.FC = () => {
  const [subInfo, setSubInfo] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  const loadSubscriptionInfo = () => {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const now = Date.now();
      const expiry = user.subscriptionExpiry || null;
      const isExpired = expiry ? expiry < now : false;
      const daysRemaining = expiry ? Math.max(0, Math.ceil((expiry - now) / (24 * 60 * 60 * 1000))) : 0;

      let plan: SubscriptionInfo['plan'] = 'none';
      let status: SubscriptionInfo['status'] = 'expired';

      if (user.subscriptionStatus === 'trial') {
        plan = 'trial';
        status = isExpired ? 'expired' : 'trialing';
      } else if (user.subscriptionStatus === 'active') {
        plan = user.subscriptionType === 'gift' ? 'free' : 'pro';
        status = 'active';
      } else if (user.subscriptionStatus === 'expired') {
        status = 'expired';
      }

      setSubInfo({
        plan,
        status,
        label: user.subscriptionLabel || 'No Plan',
        daysRemaining,
        expiryDate: expiry,
        type: user.subscriptionType || ''
      });
    } catch (error) {
      toast.error('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (interval: 'month' | 'year') => {
    try {
      // In web, we usually redirect to a specific package URL or handle via SDK
      await subscriptionService.purchase(interval === 'month' ? 'monthly_pro' : 'yearly_pro');
    } catch (error) {
      toast.error('Failed to initiate purchase');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleManageBilling = () => {
    const url = subscriptionService.getManagementUrl();
    window.open(url, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading billing information...</p>
        </div>
      </div>
    );
  }

  const showUpgrade = subInfo?.status === 'trialing' || subInfo?.status === 'expired' || subInfo?.plan === 'none';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <img src={logo} alt="Fibre" className="h-6 w-auto" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Fibre</h1>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Billing & Subscription
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your subscription and billing information
          </p>
        </div>

        {/* Current Status Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Current Plan</h3>
              <div className="flex items-center gap-3">
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {subInfo?.label || 'No Plan'}
                </span>
                {subInfo?.status === 'trialing' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                    <Clock size={12} /> Trial
                  </span>
                )}
                {subInfo?.status === 'active' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    <Check size={12} /> Active
                  </span>
                )}
                {subInfo?.status === 'expired' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    <AlertCircle size={12} /> Expired
                  </span>
                )}
              </div>
            </div>
            <CreditCard className="text-gray-400" size={24} />
          </div>

          {subInfo?.status === 'trialing' && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="text-blue-600 dark:text-blue-400" size={16} />
                <span className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                  {subInfo.daysRemaining} days remaining in your trial
                </span>
              </div>
              {subInfo.expiryDate && (
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1 ml-6">
                  Trial ends on {formatDate(subInfo.expiryDate)}
                </p>
              )}
            </div>
          )}

          {subInfo?.status === 'expired' && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="text-red-600 dark:text-red-400" size={16} />
                <span className="text-sm text-red-800 dark:text-red-200 font-medium">
                  Your trial has expired. Upgrade to continue using Fibre.
                </span>
              </div>
            </div>
          )}

          {subInfo?.status === 'active' && subInfo.expiryDate && (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Valid until</span>
                <span className="text-gray-900 dark:text-white font-medium">{formatDate(subInfo.expiryDate)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        {showUpgrade && (
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">
              {subInfo?.status === 'expired' ? 'Upgrade to Continue' : 'Choose Your Plan'}
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {/* Monthly Plan */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="text-indigo-600" size={20} />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">Monthly</h4>
                </div>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">$4.99</span>
                  <span className="text-gray-500 dark:text-gray-400">/month</span>
                </div>
                <ul className="space-y-2.5 mb-6 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> Unlimited boards</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> File attachments</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> Advanced exports</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> Priority support</li>
                </ul>
                <button
                  onClick={() => handleSubscribe('month')}
                  className="w-full py-2.5 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Subscribe Monthly
                </button>
              </div>

              {/* Yearly Plan */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-indigo-500 dark:border-indigo-400 p-6 relative">
                <div className="absolute -top-3 right-4 px-3 py-0.5 bg-indigo-600 text-white text-xs font-bold rounded-full">
                  SAVE 17%
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="text-indigo-600" size={20} />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">Yearly</h4>
                </div>
                <div className="mb-1">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">$49.99</span>
                  <span className="text-gray-500 dark:text-gray-400">/year</span>
                </div>
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-4">Just $4.17/month</p>
                <ul className="space-y-2.5 mb-6 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> Everything in Monthly</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> 2 months free</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> Early access to new features</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-green-500" /> Priority support</li>
                </ul>
                <button
                  onClick={() => handleSubscribe('year')}
                  className="w-full py-2.5 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Subscribe Yearly
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Billing Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Manage Subscription</h3>
          <div className="space-y-4">
            <div>
              <button
                onClick={handleManageBilling}
                className="inline-flex items-center gap-2 px-4 py-2 bg-black dark:bg-white text-white dark:text-gray-900 font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-200"
              >
                <ExternalLink size={16} />
                Manage Billing
              </button>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Update payment method, download invoices, and more
              </p>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
          <Shield size={14} className="inline mr-1" />
          Payments are securely processed. Cancel anytime.
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
